import React, { useState, useEffect, useMemo, useRef } from 'react';
import { VscAdd, VscTrash, VscSave, VscSearch, VscClose, VscCloudUpload, VscChevronRight } from 'react-icons/vsc';
import { getQuote, updateQuote, getQuotePrices, parseSepFile } from '../../api/quotes';
import { getCables } from '../../api/cables';
import { getItems } from '../../api/items';
import PrintButton from '../common/PrintButton';
import { buildQuotePdf } from '../../utils/pdfUtils';
import { quoteTotals, cableTypeRows, withMarkup, money, VAT_RATE } from '../../utils/quoteTotals';
import { buildCableGroups, matchesGroupFilter, GroupFilterSelects, NO_GROUP_FILTER } from '../../utils/listGroups';
import { setTabDirty } from '../../utils/unsavedTabs';
import { announceQuotesChanged, QUOTES_CHANGED } from './QuoteSidebar';
import './Quotes.css';

const EDITABLE = ['name', 'date', 'cableMarkup', 'itemMarkup', 'discount', 'vat', 'cables', 'cablePrices', 'itemGroups', 'rates', 'hours'];
const draftOf  = (q) => JSON.parse(JSON.stringify(Object.fromEntries(EDITABLE.map(k => [k, q[k]]))));
const isoDate  = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const newKey   = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// "" stays empty (no price yet); anything else becomes a number
const toNum = (v) => (v === '' || v == null ? null : Number(String(v).replace(',', '.')));

// Text end of a cable for the snapshot: breaker (switchboard) or the item's description
function cableEnd(cable, end) {
  const brk  = end === 'from' ? cable.fromBreaker : cable.toBreaker;
  const item = end === 'from' ? cable.from : cable.to;
  if (brk) return `${brk.switchboard?.name ? `${brk.switchboard.name} ` : ''}${brk.tag}`;
  return item?.description || '';
}

function NumInput({ value, onChange, className = '', ...rest }) {
  return (
    <input type="number" step="any" className={`q-input q-num ${className}`} value={value ?? ''}
      onChange={e => onChange(toNum(e.target.value))} onFocus={e => e.target.select()} {...rest} />
  );
}

// ── Cable picker ─────────────────────────────────────────────────────────────

function CablePicker({ allCables, allItems, inQuote, onAdd, onClose }) {
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState(NO_GROUP_FILTER);
  const [picked, setPicked]   = useState(() => new Set());
  const groups = useMemo(() => buildCableGroups(allCables, allItems), [allCables, allItems]);

  const shown = allCables.filter(c => {
    if (!matchesGroupFilter(groups.groups, c._id, filter)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.tag, c.cableType?.name, cableEnd(c, 'from'), cableEnd(c, 'to')].some(v => v?.toLowerCase().includes(q));
  });
  const addable = shown.filter(c => !inQuote.has(c._id));

  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="q-picker">
      <div className="q-picker-bar">
        <VscSearch size={12} />
        <input className="q-input q-picker-search" placeholder="Filter cables…" value={search} autoFocus
          onChange={e => setSearch(e.target.value)} />
        <GroupFilterSelects options={groups.options} value={filter} onChange={setFilter} className="q-input group-filter" />
        <button className="btn-secondary" disabled={!addable.length}
          onClick={() => setPicked(p => new Set([...p, ...addable.map(c => c._id)]))}>Select all shown ({addable.length})</button>
        <span className="q-spacer" />
        <button className="btn-primary" disabled={!picked.size}
          onClick={() => onAdd(allCables.filter(c => picked.has(c._id)))}>Add {picked.size || ''} cable{picked.size === 1 ? '' : 's'}</button>
        <button className="btn-icon" title="Close" onClick={onClose}><VscClose size={14} /></button>
      </div>
      <div className="q-picker-list">
        {shown.map(c => {
          const already = inQuote.has(c._id);
          return (
            <label key={c._id} className={`q-picker-row${already ? ' q-muted' : ''}`}>
              <input type="checkbox" checked={already || picked.has(c._id)} disabled={already} onChange={() => toggle(c._id)} />
              <span className="q-tag">{c.tag}</span>
              <span className="q-muted q-ellipsis">{cableEnd(c, 'from') || '—'} → {cableEnd(c, 'to') || '—'}</span>
              <span className="q-muted">{c.cableType?.name || 'no type'}</span>
              <span className="q-muted q-right">{c.length_m != null ? `${c.length_m} m` : 'no length'}</span>
              {already && <span className="q-muted">in quote</span>}
            </label>
          );
        })}
        {shown.length === 0 && <div className="q-muted q-pad">No cables match.</div>}
      </div>
    </div>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────

function QuoteEditor({ quoteId, tabId, project, isActive, onOpenTab, onRename }) {
  const [quote, setQuote]   = useState(null);     // last saved version
  const [d, setD]           = useState(null);     // draft being edited
  const [prices, setPrices] = useState({ cable: {}, article: {} });
  const [allCables, setAllCables] = useState([]);
  const [allItems, setAllItems]   = useState([]);
  const [picker, setPicker]       = useState(false);
  const [showCables, setShowCables] = useState(false);
  const [status, setStatus]       = useState('');
  const [error, setError]         = useState('');
  const [deleted, setDeleted]     = useState(false);
  const [importing, setImporting] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    getQuote(quoteId).then(r => { setQuote(r.data); setD(draftOf(r.data)); }).catch(() => setError('Could not load the quote.'));
    getQuotePrices().then(r => setPrices(r.data)).catch(() => {});
  }, [quoteId]);

  useEffect(() => {
    if (!project?._id) return;
    getCables(project._id).then(r => setAllCables(r.data)).catch(() => {});
    getItems(project._id).then(r => setAllItems(r.data)).catch(() => {});
  }, [project?._id]);

  useEffect(() => {
    const onChange = (e) => { if (e.detail?.deleted === quoteId) setDeleted(true); };
    window.addEventListener(QUOTES_CHANGED, onChange);
    return () => window.removeEventListener(QUOTES_CHANGED, onChange);
  }, [quoteId]);

  const dirty = !!(quote && d) && JSON.stringify(draftOf(quote)) !== JSON.stringify(d);
  useEffect(() => { setTabDirty(tabId, dirty && !deleted); }, [tabId, dirty, deleted]);
  useEffect(() => () => setTabDirty(tabId, false), [tabId]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    if (!d || !dirty) return;
    if (!d.name?.trim()) { setError('The quote needs a name.'); return; }
    setStatus('Saving…'); setError('');
    try {
      const r = await updateQuote(quoteId, d);
      setQuote(r.data); setD(draftOf(r.data));
      setStatus('Saved');
      if (r.data.name !== quote.name) onRename?.(r.data.name);
      announceQuotesChanged({ project: project?._id });
      getQuotePrices().then(p => setPrices(p.data)).catch(() => {});
    } catch (err) {
      setStatus('');
      setError(err.response?.data?.msg || 'Could not save — check your connection.');
    }
  };

  // Ctrl+S saves the active quote
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); saveRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isActive]);

  useEffect(() => { if (dirty) setStatus(''); }, [dirty]);

  const totals = useMemo(() => (d ? quoteTotals(d) : null), [d]);
  const typeRows = useMemo(() => (d ? cableTypeRows(d) : []), [d]);
  const inQuote = useMemo(() => new Set((d?.cables || []).map(c => c.cable)), [d]);

  if (error && !d) return <div className="module-wrap"><div className="empty-state">{error}</div></div>;
  if (!d) return <div className="module-wrap"><div className="empty-state">Loading…</div></div>;

  const set = (patch) => setD(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));

  // ── Cables
  const addCables = (cables) => {
    set(prev => {
      const priced = new Set(prev.cablePrices.map(p => p.cableType));
      const newPrices = [];
      const rows = cables.map(c => {
        const type = c.cableType?.name || '';
        if (!priced.has(type)) {
          priced.add(type);
          newPrices.push({ cableType: type, price: prices.cable[type] ?? null });
        }
        return { cable: c._id, tag: c.tag, cableType: type, from: cableEnd(c, 'from'), to: cableEnd(c, 'to'), length_m: c.length_m ?? null };
      });
      return { cables: [...prev.cables, ...rows], cablePrices: [...prev.cablePrices, ...newPrices] };
    });
    setPicker(false);
  };
  const setCablePrice = (type, price) => set(prev => ({
    cablePrices: prev.cablePrices.some(p => p.cableType === type)
      ? prev.cablePrices.map(p => (p.cableType === type ? { ...p, price } : p))
      : [...prev.cablePrices, { cableType: type, price }],
  }));
  const setCable = (idx, patch) => set(prev => ({ cables: prev.cables.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const removeCable = (idx) => set(prev => ({ cables: prev.cables.filter((_, i) => i !== idx) }));
  const removeCableType = (type) => {
    const n = d.cables.filter(c => (c.cableType || '') === type).length;
    if (!confirm(`Remove all ${n} cable${n === 1 ? '' : 's'} of type ${type || '(no type)'} from the quote?`)) return;
    set(prev => ({
      cables: prev.cables.filter(c => (c.cableType || '') !== type),
      cablePrices: prev.cablePrices.filter(p => p.cableType !== type),
    }));
  };

  // ── Items
  const importSep = async (files) => {
    setError('');
    for (const file of files) {
      setImporting(n => n + 1);
      try {
        const r = await parseSepFile(file);
        const name = r.data.fileName.replace(/\.sep$/i, '');
        const items = r.data.items.map(i => ({ ...i, price: prices.article[i.type] ?? null }));
        set(prev => ({ itemGroups: [...prev.itemGroups, { name, fileName: r.data.fileName, items }] }));
      } catch (err) {
        setError(err.response?.data?.msg || `Could not read ${file.name}.`);
      }
      setImporting(n => n - 1);
    }
  };
  const setGroup = (gi, patch) => set(prev => ({ itemGroups: prev.itemGroups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setItem = (gi, ii, patch) => set(prev => ({
    itemGroups: prev.itemGroups.map((g, i) => (i !== gi ? g : { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) })),
  }));
  const removeItem = (gi, ii) => set(prev => ({
    itemGroups: prev.itemGroups.map((g, i) => (i !== gi ? g : { ...g, items: g.items.filter((_, j) => j !== ii) })),
  }));
  const addItem = (gi) => set(prev => ({
    itemGroups: prev.itemGroups.map((g, i) => (i !== gi ? g : { ...g, items: [...g.items, { qty: 1, type: '', description: '', manufacturer: '', ean: '', tags: [], price: null }] })),
  }));
  const removeGroup = (gi) => {
    if (!confirm(`Remove "${d.itemGroups[gi].name || 'this group'}" and its ${d.itemGroups[gi].items.length} lines from the quote?`)) return;
    set(prev => ({ itemGroups: prev.itemGroups.filter((_, i) => i !== gi) }));
  };
  // A price typed for an article also fills the same article elsewhere in the quote, if that has no price yet
  const setArticlePrice = (gi, ii, price) => set(prev => {
    const type = prev.itemGroups[gi].items[ii].type;
    return {
      itemGroups: prev.itemGroups.map((g, i) => ({
        ...g,
        items: g.items.map((it, j) => ((i === gi && j === ii) || (type && it.type === type && it.price == null) ? { ...it, price } : it)),
      })),
    };
  });

  // ── Hours
  const setRate = (key, patch) => set(prev => ({ rates: prev.rates.map(r => (r.key === key ? { ...r, ...patch } : r)) }));
  const addRate = () => set(prev => ({ rates: [...prev.rates, { key: newKey(), name: '', rate: null }] }));
  const removeRate = (key) => {
    if (d.hours.some(h => h.rateKey === key) && !confirm('Some hour lines use this rate. Remove it anyway?')) return;
    set(prev => ({ rates: prev.rates.filter(r => r.key !== key) }));
  };
  const setHour = (idx, patch) => set(prev => ({ hours: prev.hours.map((h, i) => (i === idx ? { ...h, ...patch } : h)) }));
  const addHour = () => set(prev => ({ hours: [...prev.hours, { description: '', rateKey: prev.rates[0]?.key || '', hours: null }] }));
  const removeHour = (idx) => set(prev => ({ hours: prev.hours.filter((_, i) => i !== idx) }));
  const rateOf = (key) => d.rates.find(r => r.key === key);

  const missingCablePrices = typeRows.filter(r => r.price == null).length;
  const missingItemPrices  = d.itemGroups.reduce((s, g) => s + g.items.filter(i => i.price == null).length, 0);
  const printQuote = { ...quote, ...d };

  return (
    <div className="module-wrap">
      <div className="module-toolbar q-toolbar">
        <span className="module-toolbar-title">Quote {quote.number}</span>
        <button className="btn-primary" onClick={save} disabled={!dirty || deleted} title="Save (Ctrl+S)">
          <VscSave size={14} style={{ marginRight: 4 }} />Save
        </button>
        <PrintButton
          title={`Quote ${quote.number} — ${d.name}`}
          storageKey="quote"
          rows={[]}
          onOpenTab={onOpenTab}
          columns={[
            { key: 'cableList',    label: 'Cable list (one row per cable)', defaultOn: false },
            { key: 'manufacturer', label: 'Manufacturer' },
            { key: 'ean',          label: 'EAN', defaultOn: false },
          ]}
          chartPdf={(keys) => buildQuotePdf(printQuote, project, Object.fromEntries(keys.map(k => [k, true])))}
        />
        {dirty && <span className="q-unsaved">Unsaved changes</span>}
        {!dirty && status && <span className="q-muted">{status}</span>}
        {error && <span className="q-error">{error}</span>}
      </div>

      <div className="module-body q-body">
        {deleted && <div className="q-banner">This quote has been deleted.</div>}

        <div className="q-header">
          <label>Name
            <input className="q-input" value={d.name} onChange={e => set({ name: e.target.value })} />
          </label>
          <label>Quote no.
            <input className="q-input" value={quote.number} disabled />
          </label>
          <label>Date
            <input type="date" className="q-input" value={isoDate(d.date)} onChange={e => set({ date: e.target.value || null })} />
          </label>
        </div>

        {/* ── Cables ─────────────────────────────────────────── */}
        <section className="q-section">
          <div className="q-section-head">
            <h3>Cables</h3>
            <span className="q-muted">{d.cables.length} cable{d.cables.length === 1 ? '' : 's'}</span>
            {missingCablePrices > 0 && <span className="q-warn">{missingCablePrices} type{missingCablePrices === 1 ? '' : 's'} without price</span>}
            <span className="q-spacer" />
            <label className="q-inline">Markup %
              <NumInput value={d.cableMarkup} onChange={v => set({ cableMarkup: v ?? 0 })} className="q-pct" />
            </label>
            {!picker && <button className="btn-secondary" onClick={() => setPicker(true)}><VscAdd size={13} style={{ marginRight: 4 }} />Add cables</button>}
          </div>

          {picker && <CablePicker allCables={allCables} allItems={allItems} inQuote={inQuote} onAdd={addCables} onClose={() => setPicker(false)} />}

          {typeRows.length > 0 && (
            <div className="q-table-wrap">
              <table className="data-table q-table">
                <thead>
                  <tr><th>Cable type</th><th className="q-r">Cables</th><th className="q-r">Length [m]</th><th className="q-r">Price/m</th><th className="q-r">Cost</th><th className="q-r">Incl. markup</th><th /></tr>
                </thead>
                <tbody>
                  {typeRows.map(r => (
                    <tr key={r.cableType}>
                      <td>{r.cableType || <span className="q-muted">(no type)</span>}</td>
                      <td className="q-r">{r.count}</td>
                      <td className="q-r">
                        {money(r.length).replace(/,00$/, '')}
                        {r.missingLength > 0 && <span className="q-warn" title="Cables without length count as 0 m"> ({r.missingLength} without length)</span>}
                      </td>
                      <td className="q-r"><NumInput value={r.price} onChange={v => setCablePrice(r.cableType, v)} className={r.price == null ? 'q-missing' : ''} /></td>
                      <td className="q-r">{money((r.price || 0) * r.length)}</td>
                      <td className="q-r">{money(withMarkup(r.price, d.cableMarkup) * r.length)}</td>
                      <td className="q-actions"><button className="btn-icon" title="Remove these cables" onClick={() => removeCableType(r.cableType)}><VscTrash size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4}>Total cables</td><td className="q-r">{money(totals.cableCost)}</td><td className="q-r">{money(totals.cables)}</td><td /></tr>
                </tfoot>
              </table>
            </div>
          )}

          {d.cables.length > 0 && (
            <>
              <button className="q-toggle" onClick={() => setShowCables(s => !s)}>
                <VscChevronRight size={12} className={showCables ? 'q-open' : ''} /> {showCables ? 'Hide' : 'Show'} the cables ({d.cables.length})
              </button>
              {showCables && (
                <div className="q-table-wrap">
                  <table className="data-table q-table">
                    <thead><tr><th>Cable</th><th>Type</th><th>From</th><th>To</th><th className="q-r">Length [m]</th><th /></tr></thead>
                    <tbody>
                      {d.cables.map((c, i) => (
                        <tr key={`${c.cable}-${i}`}>
                          <td className="q-tag">{c.tag}</td>
                          <td>{c.cableType}</td>
                          <td className="q-ellipsis">{c.from}</td>
                          <td className="q-ellipsis">{c.to}</td>
                          <td className="q-r"><NumInput value={c.length_m} onChange={v => setCable(i, { length_m: v })} className={c.length_m == null ? 'q-missing' : ''} /></td>
                          <td className="q-actions"><button className="btn-icon" title="Remove" onClick={() => removeCable(i)}><VscTrash size={12} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {!d.cables.length && !picker && <div className="q-muted q-pad">No cables yet — press Add cables to pick them from the cable list.</div>}
        </section>

        {/* ── Items ──────────────────────────────────────────── */}
        <section className="q-section">
          <div className="q-section-head">
            <h3>Items</h3>
            {missingItemPrices > 0 && <span className="q-warn">{missingItemPrices} line{missingItemPrices === 1 ? '' : 's'} without price</span>}
            <span className="q-spacer" />
            <label className="q-inline">Markup %
              <NumInput value={d.itemMarkup} onChange={v => set({ itemMarkup: v ?? 0 })} className="q-pct" />
            </label>
            <input ref={fileRef} type="file" accept=".sep" multiple hidden
              onChange={e => { importSep([...e.target.files]); e.target.value = ''; }} />
            <button className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={importing > 0}>
              <VscCloudUpload size={13} style={{ marginRight: 4 }} />{importing > 0 ? 'Reading…' : 'Import .sep'}
            </button>
            <button className="btn-secondary" onClick={() => set(prev => ({ itemGroups: [...prev.itemGroups, { name: 'Other materials', fileName: '', items: [] }] }))}>
              <VscAdd size={13} style={{ marginRight: 4 }} />Add group
            </button>
          </div>

          {d.itemGroups.map((g, gi) => {
            const groupCost = g.items.reduce((s, i) => s + (i.qty || 0) * (i.price || 0), 0);
            return (
              <div key={gi} className="q-group">
                <div className="q-group-head">
                  <input className="q-input q-group-name" value={g.name} onChange={e => setGroup(gi, { name: e.target.value })} />
                  {g.fileName && <span className="q-muted q-ellipsis">{g.fileName}</span>}
                  <span className="q-spacer" />
                  <button className="btn-icon" title="Remove group" onClick={() => removeGroup(gi)}><VscTrash size={13} /></button>
                </div>
                <div className="q-table-wrap">
                  <table className="data-table q-table">
                    <thead>
                      <tr><th className="q-r">Qty</th><th>Article</th><th>Description</th><th>Manufacturer</th><th>EAN</th><th className="q-r">Price</th><th className="q-r">Cost</th><th /></tr>
                    </thead>
                    <tbody>
                      {g.items.map((it, ii) => (
                        <tr key={ii}>
                          <td className="q-r" title={it.tags?.length ? `Counted from: ${it.tags.join(', ')}` : undefined}>
                            <NumInput value={it.qty} onChange={v => setItem(gi, ii, { qty: v })} className="q-qty" />
                          </td>
                          <td><input className="q-input" value={it.type || ''} onChange={e => setItem(gi, ii, { type: e.target.value })} /></td>
                          <td><input className="q-input q-wide" value={it.description || ''} onChange={e => setItem(gi, ii, { description: e.target.value })} /></td>
                          <td><input className="q-input" value={it.manufacturer || ''} onChange={e => setItem(gi, ii, { manufacturer: e.target.value })} /></td>
                          <td><input className="q-input q-ean" value={it.ean || ''} onChange={e => setItem(gi, ii, { ean: e.target.value })} /></td>
                          <td className="q-r"><NumInput value={it.price} onChange={v => setArticlePrice(gi, ii, v)} className={it.price == null ? 'q-missing' : ''} /></td>
                          <td className="q-r">{money((it.qty || 0) * (it.price || 0))}</td>
                          <td className="q-actions"><button className="btn-icon" title="Remove line" onClick={() => removeItem(gi, ii)}><VscTrash size={12} /></button></td>
                        </tr>
                      ))}
                      {g.items.length === 0 && <tr><td colSpan={8} className="q-muted">No lines.</td></tr>}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={6}><button className="q-link" onClick={() => addItem(gi)}><VscAdd size={12} /> Add line</button></td>
                        <td className="q-r">{money(groupCost)}</td><td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}
          {!d.itemGroups.length && <div className="q-muted q-pad">No items yet — import the parts list from one or more SEE Electrical .sep files, or add a group by hand.</div>}
        </section>

        {/* ── Hours ──────────────────────────────────────────── */}
        <section className="q-section">
          <div className="q-section-head"><h3>Hours</h3></div>
          <div className="q-hours">
            <div className="q-table-wrap q-rates">
              <table className="data-table q-table">
                <thead><tr><th>Rate</th><th className="q-r">Per hour</th><th /></tr></thead>
                <tbody>
                  {d.rates.map(r => (
                    <tr key={r.key}>
                      <td><input className="q-input" value={r.name || ''} placeholder="e.g. Electrician" onChange={e => setRate(r.key, { name: e.target.value })} /></td>
                      <td className="q-r"><NumInput value={r.rate} onChange={v => setRate(r.key, { rate: v })} className={r.rate == null ? 'q-missing' : ''} /></td>
                      <td className="q-actions"><button className="btn-icon" title="Remove rate" onClick={() => removeRate(r.key)}><VscTrash size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={3}><button className="q-link" onClick={addRate}><VscAdd size={12} /> Add rate</button></td></tr></tfoot>
              </table>
            </div>
            <div className="q-table-wrap q-hour-lines">
              <table className="data-table q-table">
                <thead><tr><th>Description</th><th>Rate</th><th className="q-r">Hours</th><th className="q-r">Amount</th><th /></tr></thead>
                <tbody>
                  {d.hours.map((h, i) => (
                    <tr key={i}>
                      <td><input className="q-input q-wide" value={h.description || ''} placeholder="e.g. Cable pulling" onChange={e => setHour(i, { description: e.target.value })} /></td>
                      <td>
                        <select className="q-input" value={h.rateKey || ''} onChange={e => setHour(i, { rateKey: e.target.value })}>
                          {!rateOf(h.rateKey) && <option value="">— choose —</option>}
                          {d.rates.map(r => <option key={r.key} value={r.key}>{r.name || '(unnamed)'}</option>)}
                        </select>
                      </td>
                      <td className="q-r"><NumInput value={h.hours} onChange={v => setHour(i, { hours: v })} className="q-qty" /></td>
                      <td className="q-r">{money((h.hours || 0) * (rateOf(h.rateKey)?.rate || 0))}</td>
                      <td className="q-actions"><button className="btn-icon" title="Remove line" onClick={() => removeHour(i)}><VscTrash size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}><button className="q-link" onClick={addHour} disabled={!d.rates.length}><VscAdd size={12} /> Add hours</button></td>
                    <td className="q-r">{money(totals.hours)}</td><td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>

        {/* ── Totals ─────────────────────────────────────────── */}
        <section className="q-section q-totals">
          <div className="q-section-head"><h3>Total</h3></div>
          <table className="q-total-table">
            <tbody>
              <tr><td>Cables</td><td className="q-muted">cost {money(totals.cableCost)} + {d.cableMarkup || 0} %</td><td className="q-r">{money(totals.cables)}</td></tr>
              <tr><td>Items</td><td className="q-muted">cost {money(totals.itemCost)} + {d.itemMarkup || 0} %</td><td className="q-r">{money(totals.items)}</td></tr>
              <tr><td>Hours</td><td /><td className="q-r">{money(totals.hours)}</td></tr>
              <tr className="q-total-sub"><td>Subtotal</td><td /><td className="q-r">{money(totals.subtotal)}</td></tr>
              <tr>
                <td>Discount</td>
                <td><label className="q-inline"><NumInput value={d.discount} onChange={v => set({ discount: v ?? 0 })} className="q-pct" /> %</label></td>
                <td className="q-r">{totals.discount ? `-${money(totals.discount)}` : money(0)}</td>
              </tr>
              <tr className="q-total-sub"><td>Total excl. VAT</td><td /><td className="q-r">{money(totals.net)}</td></tr>
              <tr>
                <td><label className="q-inline"><input type="checkbox" checked={!!d.vat} onChange={e => set({ vat: e.target.checked })} /> VAT {VAT_RATE * 100} %</label></td>
                <td /><td className="q-r">{money(totals.vat)}</td>
              </tr>
              <tr className="q-total-grand"><td>Total{d.vat ? ' incl. VAT' : ''}</td><td className="q-muted">DKK</td><td className="q-r">{money(totals.total)}</td></tr>
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

export default QuoteEditor;
